import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { stats } from "@/mocks/home";

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20;
      const y = (e.clientY / window.innerHeight - 0.5) * 20;
      el.style.transform = `translate(${x}px, ${y}px)`;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/34fa092b6d49e9d316c86335fd21d24b.jpg"
          alt="AWWAL Gaming Hero"
          title="AWWAL Marine & Engineering — Game Development & Guild Operations"
          className="w-full h-full object-cover object-top opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#080808] via-[#080808]/70 to-[#080808]/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-transparent to-[#080808]/50" />
      </div>

      <div
        className="absolute inset-0 z-0 opacity-5"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,255,136,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,136,0.4) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div ref={heroRef} className="absolute right-10 top-1/4 hidden lg:block z-0 transition-transform duration-300 ease-out pointer-events-none">
        <div className="w-[500px] h-[500px] rounded-full" style={{ background: "radial-gradient(circle, rgba(0,255,136,0.06) 0%, transparent 70%)" }} />
      </div>

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-20">
        <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#00ff88]/30 rounded-sm bg-[#00ff88]/5 mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00ff88] animate-pulse" />
          <span className="text-[#00ff88] text-xs tracking-[0.3em] uppercase font-semibold font-inter">
            Game Development & Guild Operations
          </span>
        </div>

        <h1 className="text-5xl sm:text-7xl lg:text-[90px] font-black leading-none tracking-tight mb-6 max-w-3xl font-syne">
          <span className="text-white block">BUILD YOUR</span>
          <span className="block" style={{ WebkitTextStroke: "1px rgba(255,255,255,0.15)", color: "transparent" }}>
            GAMING
          </span>
          <span className="block text-[#00ff88]">EMPIRE.</span>
        </h1>

        <p className="text-white/50 text-base sm:text-lg max-w-xl leading-relaxed mb-10 font-inter">
          AWWAL Marine &amp; Engineering delivers world-class game development, elite guild operations, and powerful social platforms — crafted in Malaysia, deployed globally.
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/games-guilds"
            className="whitespace-nowrap inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#00ff88] text-[#080808] text-sm font-bold tracking-widest uppercase rounded-sm hover:bg-[#00ff88]/90 transition-all cursor-pointer font-inter"
          >
            Explore Games
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-arrow-right-line text-base leading-none" />
            </div>
          </Link>
          <Link
            to="/about"
            className="whitespace-nowrap inline-flex items-center justify-center gap-3 px-8 py-4 border border-white/20 text-white text-sm font-semibold tracking-widest uppercase rounded-sm hover:border-white/40 hover:bg-white/5 transition-all cursor-pointer font-inter"
          >
            About AWWAL
          </Link>
        </div>

        <div className="mt-20 flex flex-wrap gap-8 sm:gap-16">
          {stats.map((s) => (
            <div key={s.label}>
              <div className="text-3xl sm:text-4xl font-black text-white tracking-tight font-syne">{s.value}</div>
              <div className="text-white/35 text-xs tracking-widest uppercase mt-1 font-inter">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 opacity-40">
        <div className="w-px h-10 bg-white/30 animate-pulse" />
        <span className="text-white/40 text-[10px] tracking-widest font-inter">SCROLL</span>
      </div>
    </section>
  );
}
